import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  User, 
  Target, 
  Award,
  ChevronDown,
  ChevronUp,
  FileText,
  Briefcase,
  Shield,
  Phone,
  BarChart3,
  PenTool,
  Camera,
  Sparkles
} from "lucide-react";
import { getPublicFaq } from "@/lib/api";

const ABOUT_IMAGE = "/images/photo-1743865319071.jpg";

const BANNER_IMG = "/images/photo-1773847408152.jpg";

const processSteps = [
  {
    step: "01",
    icon: Phone,
    title: "Prise de contact",
    description: "Nous analysons votre bien et vos objectifs. Visite gratuite et estimation personnalisée.",
    detail: "Visite gratuite de votre bien"
  },
  {
    step: "02",
    icon: BarChart3,
    title: "Étude personnalisée",
    description: "Nous vous présentons une estimation détaillée de vos revenus potentiels et la formule la plus adaptée.",
    detail: "Simulation de rentabilité offerte"
  },
  {
    step: "03",
    icon: PenTool,
    title: "Signature du contrat",
    description: "Bail civil professionnel ou mandat de prestation en toute transparence, avec des conditions claires et flexibles.",
    detail: "Contrat sans engagement long"
  },
  {
    step: "04",
    icon: Camera,
    title: "Mise en location",
    description: "Création des annonces, shooting photo professionnel, rédaction optimisée et diffusion multi-plateformes.",
    detail: "En ligne sous 7 jours"
  },
  {
    step: "05",
    icon: Sparkles,
    title: "Gestion complète",
    description: "Accueil, ménage, linge, maintenance, relation voyageurs 7j/7. Vous ne faites plus rien.",
    detail: "Rapport mensuel détaillé"
  }
];

const values = [
  {
    icon: Target,
    title: "Expertise locale",
    description: "Nous connaissons chaque quartier de Limoges et chaque spécificité thermale d'Évaux-les-Bains."
  },
  {
    icon: Shield,
    title: "Zéro intermédiaire",
    description: "Vous parlez à ceux qui gèrent réellement votre bien. Aucun sous-traitant, aucune délégation."
  },
  {
    icon: Award,
    title: "Transparence radicale",
    description: "Des comptes rendus clairs, sans astérisques cachées. Rapports mensuels détaillés."
  }
];

export default function ExpertisePage() {
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    loadFaqs();
  }, []);

  const loadFaqs = async () => {
    try {
      const res = await getPublicFaq();
      setFaqs(res.data);
    } catch (error) {
      console.error("Error loading FAQs:", error);
    }
  };

  return (
    <div className="">
      {/* Hero */}
      <section className="section-hh bg-[#F5F0EB]" data-testid="expertise-hero">
        <div className="container-hh">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block bg-[#004333]/10 text-[#004333] px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-[#004333]/20">Notre expertise</span>
              <h1 className="font-display text-4xl md:text-5xl text-[#004333] mb-8">
                Le mot de la Directrice
              </h1>

              {/* Quote block */}
              <div className="relative bg-[#FFFDF9] border border-[#E0D6CA] rounded-2xl p-8 mb-8 shadow-[0_4px_24px_rgba(44,30,20,0.06)]">
                {/* Guillemet décoratif */}
                <span className="absolute -top-5 left-8 text-7xl text-[#C9922A]/20 font-serif leading-none select-none">"</span>
                <blockquote className="relative z-10">
                  <p className="text-[#2D2418] text-lg leading-relaxed italic mb-6">
                    Happy House n'est pas née dans un bureau, mais sur le terrain. Nous avons constaté que la location courte durée était souvent un casse-tête pour les propriétaires : gestion des clés, ménages aléatoires, peur des impayés... Nous avons décidé de créer la solution que nous aurions aimée trouver.
                  </p>
                  <footer className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#004333] rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#004333] text-sm">Directrice</div>
                      <div className="text-[#8B7D6B] text-sm">Happy House Conciergerie</div>
                    </div>
                  </footer>
                </blockquote>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src={ABOUT_IMAGE}
                alt="Notre équipe"
                className="w-full aspect-[4/3] object-cover rounded-xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Notre Vision */}
      <section className="section-hh bg-[#004333]" data-testid="vision-section">
        <div className="container-hh">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <span className="inline-block bg-[#C9922A]/20 text-[#C9922A] px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-[#C9922A]/30 tracking-wide">
                Notre Vision
              </span>
              <h2 className="font-display text-3xl md:text-4xl text-white">
                L'immobilier comme aventure humaine
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Pilier 1 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="relative bg-white/[0.05] border border-white/10 rounded-2xl p-7 hover:border-[#C9922A]/30 hover:bg-white/[0.08] transition-all duration-500 group"
              >
                <div className="w-10 h-10 rounded-xl bg-[#C9922A]/15 border border-[#C9922A]/25 flex items-center justify-center mb-5 group-hover:bg-[#C9922A]/25 transition-colors duration-300">
                  <User className="w-5 h-5 text-[#C9922A]" />
                </div>
                <h3 className="font-display text-lg text-white mb-3">Propriétaires libres</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Offrir aux propriétaires une liberté totale : des revenus garantis sans contrainte de gestion, en toute transparence.
                </p>
              </motion.div>

              {/* Pilier central — mis en avant */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="relative bg-[#C9922A]/10 border border-[#C9922A]/40 rounded-2xl p-7 shadow-[0_8px_40px_rgba(201,146,42,0.15)] flex flex-col"
              >
                {/* Badge en haut à l'intérieur */}
                <div className="flex justify-center mb-5">
                  <span className="bg-[#C9922A] text-[#1A120B] text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider">
                    Notre conviction
                  </span>
                </div>
                <blockquote className="flex-1">
                  <p className="text-white/90 text-base leading-relaxed italic font-light mb-5">
                    "Nous croyons que l'immobilier est avant tout une aventure humaine."
                  </p>
                </blockquote>
                <div className="pt-5 border-t border-[#C9922A]/20">
                  <p className="text-white/60 text-sm leading-relaxed">
                    Chaque logement a une âme. Chaque séjour mérite d'être un moment d'exception.
                  </p>
                </div>
              </motion.div>

              {/* Pilier 3 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="relative bg-white/[0.05] border border-white/10 rounded-2xl p-7 hover:border-[#C9922A]/30 hover:bg-white/[0.08] transition-all duration-500 group"
              >
                <div className="w-10 h-10 rounded-xl bg-[#C9922A]/15 border border-[#C9922A]/25 flex items-center justify-center mb-5 group-hover:bg-[#C9922A]/25 transition-colors duration-300">
                  <Award className="w-5 h-5 text-[#C9922A]" />
                </div>
                <h3 className="font-display text-lg text-white mb-3">Voyageurs conquis</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Garantir aux voyageurs un séjour inoubliable dans des logements soigneusement sélectionnés et entretenus.
                </p>
              </motion.div>
            </div>

            {/* Territoire */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="mt-10 flex items-start gap-4 bg-white/[0.04] border border-white/[0.08] rounded-2xl p-6"
            >
              <div className="w-10 h-10 rounded-xl bg-[#C9922A]/15 border border-[#C9922A]/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Target className="w-5 h-5 text-[#C9922A]" />
              </div>
              <div>
                <h4 className="font-display text-white text-base mb-2">Un ancrage territorial fort</h4>
                <p className="text-white/60 text-sm leading-relaxed">
                  En choisissant <strong className="text-white/80">Limoges</strong> et <strong className="text-white/80">Évaux-les-Bains</strong>, nous misons sur des territoires authentiques que nous aimons et maîtrisons parfaitement. Une expertise locale que nul gestionnaire national ne peut égaler.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Photo Banner */}
      <div className="photo-banner">
        <img src={BANNER_IMG} alt="Ambiance chaleureuse" loading="lazy" />
      </div>

      {/* Notre méthode — Timeline Verticale Interactive */}
      <section className="relative overflow-hidden bg-[#1A120B] py-20 lg:py-28" data-testid="methode-section">
        {/* Subtle noise texture */}
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"}} />
        
        {/* Ambient glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#C9922A]/[0.04] blur-[120px] pointer-events-none" />
        
        <div className="container-hh relative z-10">
          <div className="text-center mb-16 lg:mb-20">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block bg-[#C9922A]/10 text-[#C9922A] px-5 py-1.5 rounded-full text-sm font-semibold mb-5 border border-[#C9922A]/20 tracking-wide"
            >
              Processus
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-display text-3xl md:text-4xl lg:text-5xl text-white"
            >
              Notre méthode de gestion
            </motion.h2>
          </div>

          {/* Timeline Container */}
          <div className="relative max-w-4xl mx-auto">
            {/* Vertical line — golden glow */}
            <div className="absolute left-6 lg:left-1/2 top-0 bottom-0 w-px lg:-translate-x-px">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#C9922A]/30 to-transparent" />
              <motion.div
                className="absolute top-0 left-0 w-full bg-gradient-to-b from-[#C9922A] via-[#E8C874] to-[#C9922A]/0"
                initial={{ height: "0%" }}
                whileInView={{ height: "100%" }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 2, ease: "easeOut" }}
              />
            </div>

            {/* Steps */}
            <div className="relative space-y-12 lg:space-y-16">
              {processSteps.map((item, index) => {
                const Icon = item.icon;
                const isLeft = index % 2 === 0;

                return (
                  <motion.div
                    key={item.step}
                    initial={{ opacity: 0, x: isLeft ? -40 : 40, y: 20 }}
                    whileInView={{ opacity: 1, x: 0, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, delay: index * 0.12, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className={`relative flex items-start gap-6 lg:gap-0 ${isLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
                    data-testid={`process-step-${item.step}`}
                  >
                    {/* Node circle on the line */}
                    <div className="absolute left-6 lg:left-1/2 -translate-x-1/2 z-20 flex items-center justify-center">
                      <motion.div
                        whileInView={{ scale: [0, 1.2, 1] }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.15 + 0.3, duration: 0.5 }}
                        className="w-12 h-12 rounded-full border-2 border-[#C9922A] bg-[#1A120B] flex items-center justify-center shadow-[0_0_20px_rgba(201,146,42,0.3)]"
                      >
                        <span className="font-mono text-[#C9922A] text-sm font-bold">{item.step}</span>
                      </motion.div>
                    </div>

                    {/* Card */}
                    <div className={`ml-16 lg:ml-0 lg:w-[calc(50%-40px)] ${isLeft ? 'lg:pr-0' : 'lg:pl-0'}`}>
                      <motion.div
                        whileHover={{ y: -4, scale: 1.01 }}
                        transition={{ duration: 0.3 }}
                        className="group relative p-6 lg:p-7 rounded-2xl bg-white/[0.04] backdrop-blur-md border border-white/[0.08] hover:border-[#C9922A]/30 transition-all duration-500 hover:bg-white/[0.07] hover:shadow-[0_8px_40px_rgba(201,146,42,0.1)]"
                      >
                        {/* Glow on hover */}
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#C9922A]/0 to-[#C9922A]/0 group-hover:from-[#C9922A]/[0.03] group-hover:to-transparent transition-all duration-500 pointer-events-none" />

                        <div className="relative z-10">
                          {/* Icon + Title */}
                          <div className="flex items-center gap-3.5 mb-3">
                            <div className="w-10 h-10 rounded-xl bg-[#C9922A]/10 border border-[#C9922A]/20 flex items-center justify-center group-hover:bg-[#C9922A]/15 group-hover:border-[#C9922A]/30 transition-all duration-300">
                              <Icon className="w-5 h-5 text-[#C9922A]" />
                            </div>
                            <h3 className="font-display text-lg text-white group-hover:text-[#E8C874] transition-colors duration-300">
                              {item.title}
                            </h3>
                          </div>
                          
                          {/* Description */}
                          <p className="text-white/50 text-sm leading-relaxed mb-3">
                            {item.description}
                          </p>

                          {/* Detail tag */}
                          <span className="inline-flex items-center gap-1.5 text-[#C9922A]/70 text-xs font-medium tracking-wide uppercase group-hover:text-[#C9922A] transition-colors duration-300">
                            <span className="w-1 h-1 rounded-full bg-[#C9922A]/50 group-hover:bg-[#C9922A] transition-colors duration-300" />
                            {item.detail}
                          </span>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Valeurs */}
      <section className="section-hh bg-[#2C1E14]" data-testid="valeurs-section">
        <div className="container-hh">
          <div className="text-center mb-16">
            <span className="inline-block bg-[#C9922A]/20 text-[#C9922A] px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-[#C9922A]/30">Nos valeurs</span>
            <h2 className="font-display text-3xl md:text-4xl text-white">
              Pourquoi nous faire confiance
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/5 p-8 backdrop-blur-sm rounded-xl border border-white/10 hover:border-[#C9922A]/30 transition-all duration-300"
                >
                  <Icon className="w-10 h-10 text-[#C9922A] mb-4" />
                  <h3 className="font-display text-xl text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="text-white/70">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-14"
          >
            <Link
              to="/voyageurs"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-[#C9922A] to-[#E8C874] text-[#1A120B] font-semibold rounded-full hover:shadow-[0_4px_24px_rgba(201,146,42,0.4)] transition-all duration-300 text-sm uppercase tracking-wider"
            >
              Réserver un logement
            </Link>
            <Link
              to="/proprietaires#contact-form"
              className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-white/30 text-white font-semibold rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 text-sm uppercase tracking-wider"
            >
              Confier mon bien
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Bail civil expliqué */}
      <section className="section-hh bg-[#F5F0EB]" data-testid="bail-section">
        <div className="container-hh">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block bg-[#004333]/10 text-[#004333] px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-[#004333]/20">Juridique</span>
              <h2 className="font-display text-3xl md:text-4xl text-[#004333] mb-6">
                Le bail civil expliqué
              </h2>
            </div>
            
            <div className="bg-[#FFFDF9] p-8 md:p-12 rounded-xl shadow-[0_4px_24px_rgba(44,30,20,0.08)] border border-[#E0D6CA]">
              <div className="flex items-start gap-4 mb-8">
                <div className="w-12 h-12 bg-[#C9922A]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-[#C9922A]" />
                </div>
                <div>
                  <h3 className="font-display text-xl text-[#004333] mb-2">
                    Qu'est-ce que la sous-location professionnelle ?
                  </h3>
                  <p className="text-[#6B5E50]">
                    La sous-location professionnelle permet d'exploiter un logement en location courte durée avec 
                    une gestion entièrement prise en charge. Vous louez votre bien de Limoges et Évaux-les-Bains 
                    à Happy House via un bail Code civil professionnel. Nous devenons votre locataire unique et 
                    prenons en charge la gestion complète de l'hébergement de nos voyageurs.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 mb-8">
                <div className="w-12 h-12 bg-[#C9922A]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-6 h-6 text-[#C9922A]" />
                </div>
                <div>
                  <h3 className="font-display text-xl text-[#004333] mb-2">
                    Les avantages pour vous
                  </h3>
                  <ul className="text-[#6B5E50] space-y-2">
                    <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#C9922A]" /> Paiement garanti : votre loyer est versé à date fixe, même si le logement est inoccupé</li>
                    <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#C9922A]" /> Entretien rigoureux : nous remettons le bien à neuf après chaque voyageur</li>
                    <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#C9922A]" /> Zéro frais : pas de frais de gestion, pas de commissions. Le loyer est net pour vous</li>
                    <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#C9922A]" /> Flexibilité du contrat</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#C9922A]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-[#C9922A]" />
                </div>
                <div>
                  <h3 className="font-display text-xl text-[#004333] mb-2">
                    Sécurité et transparence
                  </h3>
                  <p className="text-[#6B5E50]">
                    Vous recevez chaque mois un rapport détaillé : réservations, revenus, 
                    prestations effectuées, état du bien. Une transparence totale sur la 
                    gestion de votre patrimoine.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-hh bg-[#EDE8E1]" data-testid="faq-section">
        <div className="container-hh">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block bg-[#C9922A]/15 text-[#C9922A] px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-[#C9922A]/25">FAQ</span>
              <h2 className="font-display text-3xl md:text-4xl text-[#004333]">
                Questions fréquentes
              </h2>
            </div>
            
            <div className="space-y-3">
              {faqs.map((faq) => (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  className="border border-[#E0D6CA] bg-[#FFFDF9] rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-[#EDE8E1]/50 transition-colors"
                    data-testid={`faq-${faq.id}`}
                  >
                    <span className="font-semibold text-[#004333] pr-4">
                      {faq.question}
                    </span>
                    {openFaq === faq.id ? (
                      <ChevronUp className="w-5 h-5 text-[#C9922A] flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[#C9922A] flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === faq.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-6 pb-6"
                    >
                      <p className="text-[#6B5E50] leading-relaxed border-t border-[#E8E0D4] pt-4">
                        {faq.reponse}
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
